/**
 * 管理画面「紙から取り込み」
 *
 * 目的は集客ではなく **活性化(activation)**。
 * 登録直後は報告0件でダッシュボードが空になり、価値が見える前に離脱する。
 * 手元にある紙のヒヤリハット・点検表を数枚アップロードするだけで、
 * AIが匿名化＋4M分析まで済ませたケースがダッシュボードに並ぶ状態を作る。
 *
 * 原則:
 * - AIの結果は「ドラフト」。**確定は必ず管理者の操作**（human-in-the-loop）
 * - 原文（OCRテキスト）と整形結果を並べて確認できる
 * - 1枚ごとに独立処理。1枚失敗しても他は進む
 */
import React, { useCallback, useRef, useState } from 'react';
import {
  Upload, FileImage, Loader2, CheckCircle2, AlertTriangle, X, Trash2,
  ChevronDown, ChevronUp, Sparkles, ArrowUpRight, FileText, ShieldCheck, Layers, RotateCw,
} from 'lucide-react';
import { importFromImage } from '../services/ocrService';
import { isPdfFile, pdfToPageImages, PDF_MAX_PAGES } from '../services/pdfService';
import { createCase } from '../services/supabase';
import { AIAnalysisResult, RiskLevel, Category } from '../types';

type DraftStatus = 'queued' | 'processing' | 'ready' | 'failed' | 'saved';

interface Draft {
  id: string;
  fileName: string;
  previewUrl: string;
  status: DraftStatus;
  /** 再試行のために元画像を保持する。PDFはページ画像化済みのFileが入る。 */
  file?: File;
  /** 通信断など、同じ画像でやり直せば成功しうる失敗 */
  retryable?: boolean;
  /** 失敗理由 or 上限到達メッセージ */
  message?: string;
  limitExceeded?: boolean;
  ocrText?: string;
  documentType?: string;
  analysis?: AIAnalysisResult;
}

interface Props {
  organizationId?: string;
  teamId?: string;
  /** 保存完了後にケース一覧へ促す */
  onGoToCases: () => void;
  onGoToSettings: () => void;
  /** 保存できたケースを親のstateへ反映（ダッシュボードを即座に埋めるため） */
  onCaseCreated?: () => void;
}

const RISK_STYLE: Record<string, string> = {
  High: 'bg-red-100 text-red-800 border-red-200',
  Medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  Low: 'bg-green-100 text-green-800 border-green-200',
};

export const ManagerImportView: React.FC<Props> = ({
  organizationId, teamId, onGoToCases, onGoToSettings, onCaseCreated,
}) => {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [savingAll, setSavingAll] = useState(false);
  /** 今月の取り込み枚数。OCR専用の独立枠（QR報告のAI分析枠は消費しない） */
  const [usage, setUsage] = useState<{ used: number; limit: number } | null>(null);
  /** 画像・PDFとして扱えず受け付けなかったファイル名。無言で捨てないための表示用 */
  const [rejected, setRejected] = useState<string[]>([]);
  /** PDFをページ画像に分解している最中の表示（変換はブラウザ内で行う） */
  const [pdfProgress, setPdfProgress] = useState<{ name: string; page: number; total: number } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const update = (id: string, patch: Partial<Draft>) =>
    setDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));

  /** 画像1枚をOCRにかけ、結果を該当ドラフトへ反映する（初回・再試行で共通） */
  const ocrInto = useCallback(async (draftId: string, image: File, orgId: string) => {
    update(draftId, { status: 'processing', message: undefined, retryable: undefined });
    const res = await importFromImage(image, orgId);
    if (res.success) {
      if (res.usage) setUsage(res.usage);
      update(draftId, {
        status: 'ready',
        ocrText: res.ocrText,
        documentType: res.documentType,
        analysis: res.analysis,
      });
    } else {
      update(draftId, {
        status: 'failed',
        message: res.message,
        limitExceeded: res.limitExceeded,
        retryable: res.retryable,
        ocrText: res.ocrText,
      });
    }
  }, []);

  /** 失敗した1枚だけをやり直す。PDF1本を丸ごと入れ直させない（枠も無駄に消費させない）。 */
  const retryOne = useCallback(async (d: Draft) => {
    if (!organizationId || !d.file) return;
    await ocrInto(d.id, d.file, organizationId);
  }, [organizationId, ocrInto]);

  /** 画像1枚ずつOCRにかけてドラフト化する（同時実行しない: レート制限に当たる） */
  const runOcr = useCallback(async (images: File[], orgId: string) => {
    const newDrafts: Draft[] = images.map((f, i) => ({
      id: `${Date.now()}_${i}_${f.name}`,
      fileName: f.name,
      previewUrl: URL.createObjectURL(f),
      status: 'queued',
      file: f,
    }));
    setDrafts((prev) => [...prev, ...newDrafts]);

    for (let i = 0; i < images.length; i++) {
      await ocrInto(newDrafts[i].id, images[i], orgId);
    }
  }, [ocrInto]);

  const processFiles = useCallback(async (files: File[]) => {
    if (!organizationId) return;

    // ドラッグ&ドロップは accept 属性を無視するため、どんなファイルでも渡ってくる。
    // 受け付けないものは無言で捨てず、必ず理由を表示する。
    const images = files.filter((f) => f.type.startsWith('image/'));
    const pdfs = files.filter((f) => isPdfFile(f));
    const others = files.filter((f) => !f.type.startsWith('image/') && !isPdfFile(f));
    setRejected(others.map((f) => f.name));

    if (images.length > 0) {
      await runOcr(images, organizationId);
    }

    // PDFはページごとの画像に分解してから、画像と同じ経路に載せる。
    // 変換はすべてブラウザ内で行い、PDF原本はサーバーへ送らない。
    for (const pdf of pdfs) {
      let pages: File[] = [];
      try {
        setPdfProgress({ name: pdf.name, page: 0, total: 0 });
        const converted = await pdfToPageImages(pdf, (p) =>
          setPdfProgress({ name: pdf.name, page: p.page, total: p.total })
        );
        pages = converted.images;

        // 何ページ消費するかを必ず事前に見せる（枠を黙って食わせない）
        const skipNote = converted.skippedPages > 0
          ? `\n\n※ このPDFは${converted.totalPages}ページありますが、1回に取り込めるのは${PDF_MAX_PAGES}ページまでです。`
            + `最初の${PDF_MAX_PAGES}ページのみ取り込みます（残り${converted.skippedPages}ページは分割して再度アップロードしてください）。`
          : '';
        const ok = window.confirm(
          `「${pdf.name}」を${pages.length}ページ取り込みます。\n`
          + `今月の取り込み枠を${pages.length}ページ分消費します。${skipNote}\n\n続けますか？`
        );
        if (!ok) { pages = []; }
      } catch (e) {
        setRejected((prev) => [...prev, `${pdf.name}（${e instanceof Error ? e.message : '変換に失敗しました'}）`]);
      } finally {
        setPdfProgress(null);
      }
      if (pages.length > 0) {
        await runOcr(pages, organizationId);
      }
    }
  }, [organizationId, runOcr]);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    processFiles(Array.from(e.dataTransfer.files || []));
  };

  const editAnalysis = (id: string, patch: Partial<AIAnalysisResult>) =>
    setDrafts((prev) => prev.map((d) =>
      d.id === id && d.analysis ? { ...d, analysis: { ...d.analysis, ...patch } } : d));

  const saveOne = async (d: Draft): Promise<boolean> => {
    if (!organizationId || !d.analysis) return false;
    const created = await createCase({
      organizationId,
      teamId,
      title: d.analysis.title,
      body: d.analysis.formattedBody,
      // 生のOCRテキスト(d.ocrText)は**保存しない**。実名など匿名化前の情報を含むため、
      // 画面での確認用途に限りブラウザのメモリ上だけで扱う。DBには匿名化済み本文のみ。
      originalText: d.analysis.formattedBody,
      riskLevel: d.analysis.riskLevel,
      category1: d.analysis.category1,
      category2: d.analysis.category2,
      lesson: d.analysis.lesson,
      hasAudio: false,
    });
    if (created) {
      update(d.id, { status: 'saved' });
      onCaseCreated?.();
      return true;
    }
    update(d.id, { status: 'failed', message: '保存に失敗しました。再度お試しください。' });
    return false;
  };

  const saveAll = async () => {
    setSavingAll(true);
    for (const d of drafts.filter((x) => x.status === 'ready')) {
      await saveOne(d);
    }
    setSavingAll(false);
  };

  const remove = (id: string) => {
    const d = drafts.find((x) => x.id === id);
    if (d) URL.revokeObjectURL(d.previewUrl);
    setDrafts((prev) => prev.filter((x) => x.id !== id));
  };

  const readyCount = drafts.filter((d) => d.status === 'ready').length;
  const savedCount = drafts.filter((d) => d.status === 'saved').length;
  const busy = drafts.some((d) => d.status === 'processing' || d.status === 'queued');
  const hitLimit = drafts.some((d) => d.limitExceeded);

  return (
    <div className="max-w-5xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-industrial-900">紙から取り込み</h1>
        <p className="mt-1 text-slate-600 text-sm leading-relaxed">
          紙のヒヤリハット報告書・点検表・作業日報を撮影してアップロードすると、AIが文字を読み取り、
          <strong className="text-industrial-900">個人名を自動で匿名化</strong>して4M分析まで行います。
          内容を確認してから登録するので、そのままケース一覧・ダッシュボードに反映されます。
        </p>
      </div>

      {/* アップロード領域 */}
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        className={`cursor-pointer rounded-2xl border-2 border-dashed p-8 text-center transition ${
          isDragging ? 'border-safety-500 bg-orange-50' : 'border-slate-300 bg-white hover:bg-slate-50'
        }`}
      >
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf"
          multiple
          className="hidden"
          onChange={(e) => {
            processFiles(Array.from(e.target.files || []));
            if (fileRef.current) fileRef.current.value = '';
          }}
        />
        <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-xl bg-orange-50 text-safety-600">
          <Upload size={26} />
        </div>
        <p className="text-base font-bold text-industrial-900">
          紙の書類・スキャンPDFをここにドラッグ、またはクリックして選択
        </p>
        <p className="mt-1.5 text-sm text-slate-600">
          PDF（複数ページOK）/ JPEG / PNG / HEIC・まとめて投入OK・画像1枚あたり約6MBまで
        </p>
      </div>

      {/* 取り込み前に必ず伝えたい2点。
          ドロップ枠の中に小さい薄字で置いていたため誰にも読まれていなかった。
          特に「保存しない」はこの機能を安心して使えるかの判断材料そのものなので、
          枠の外に出してアイコン付きで独立させる。 */}
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <ShieldCheck className="mt-0.5 shrink-0 text-emerald-600" size={20} />
          <div>
            <p className="text-sm font-bold text-emerald-900">画像も原文もサーバーに保存しません</p>
            <p className="mt-1 text-xs leading-relaxed text-emerald-800">
              アップロードした画像とPDFは読み取り後すぐに破棄されます。
              登録されるのは<strong>氏名を伏せ字にした後の内容だけ</strong>です。
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4">
          <Layers className="mt-0.5 shrink-0 text-safety-600" size={20} />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-industrial-900">QR報告のAI分析枠とは別枠です</p>
            <p className="mt-1 text-xs leading-relaxed text-slate-600">
              紙1枚（PDFは1ページ）＝1ページ分。何枚取り込んでも、
              現場からのQR報告の分析回数は減りません。
            </p>
            {usage && (
              <div className="mt-2">
                <div className="flex items-baseline justify-between">
                  <span className="text-xs text-slate-500">今月の取り込み</span>
                  <span className="text-xs font-bold text-industrial-900">
                    {usage.used} / {usage.limit} ページ
                  </span>
                </div>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-safety-500 transition-all"
                    style={{ width: `${Math.min(100, (usage.used / usage.limit) * 100)}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {pdfProgress && (
        <div className="mt-4 flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
          <Loader2 className="shrink-0 animate-spin text-blue-600" size={18} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-bold text-blue-900">
              PDFをページごとの画像に変換中… {pdfProgress.name}
            </p>
            <p className="mt-0.5 text-xs text-blue-800">
              {pdfProgress.total > 0
                ? `${pdfProgress.page} / ${pdfProgress.total} ページ`
                : 'ページ数を確認しています…'}
              ・変換はこのブラウザ内で行われ、PDFそのものはサーバーに送信されません
            </p>
          </div>
        </div>
      )}

      {rejected.length > 0 && (
        <div className="mt-4 flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <FileImage className="mt-0.5 shrink-0 text-slate-400" size={18} />
          <div className="flex-1">
            <p className="text-sm font-bold text-industrial-900">
              取り込めなかったファイルがあります
            </p>
            <p className="mt-1 text-xs text-slate-600">
              {rejected.slice(0, 3).join('、')}
              {rejected.length > 3 && ` ほか${rejected.length - 3}件`}
            </p>
            <p className="mt-2 text-xs leading-relaxed text-slate-600">
              対応しているのは<strong>PDF</strong>と<strong>画像</strong>（JPEG / PNG / HEIC）です。
              Excel・Wordの資料は、そのソフトの「<strong>PDFで保存</strong>」または「印刷 → PDFに出力」で
              PDFにしてからアップロードしてください。
            </p>
          </div>
          <button
            onClick={() => setRejected([])}
            className="shrink-0 rounded p-1 text-slate-400 hover:bg-slate-200"
            aria-label="閉じる"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {hitLimit && (
        <div className="mt-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <AlertTriangle className="mt-0.5 shrink-0 text-amber-600" size={18} />
          <div className="flex-1">
            <p className="text-sm font-bold text-amber-900">今月の取り込み上限に達しました</p>
            <p className="mt-1 text-xs text-amber-800">
              上限は毎月1日にリセットされます。プランを変更すると、今月中でも続けて取り込めます。
            </p>
            <button
              onClick={onGoToSettings}
              className="mt-2 inline-flex items-center gap-1 rounded-lg bg-safety-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-safety-700"
            >
              プランを見る <ArrowUpRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* ドラフト一覧 */}
      {drafts.length > 0 && (
        <div className="mt-6">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-600">
              {busy ? '読み取り中…' : `確認待ち ${readyCount}件`}
              {savedCount > 0 && <span className="ml-2 text-emerald-700">／ 登録済み {savedCount}件</span>}
            </p>
            <div className="flex items-center gap-2">
              {savedCount > 0 && (
                <button
                  onClick={onGoToCases}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  ケース一覧で確認
                </button>
              )}
              <button
                onClick={saveAll}
                disabled={readyCount === 0 || savingAll || busy}
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-bold text-white transition ${
                  readyCount === 0 || savingAll || busy
                    ? 'cursor-not-allowed bg-slate-300'
                    : 'bg-safety-600 hover:bg-safety-700'
                }`}
              >
                {savingAll ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                確認した{readyCount > 0 ? `${readyCount}件` : ''}を登録
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {drafts.map((d) => (
              <div key={d.id} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                <div className="flex items-start gap-4 p-4">
                  <img
                    src={d.previewUrl}
                    alt=""
                    className="h-24 w-20 shrink-0 rounded-lg border border-slate-200 object-cover"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="mb-2 flex items-center gap-2">
                      <FileImage size={14} className="shrink-0 text-slate-400" />
                      <span className="truncate text-xs text-slate-500">{d.fileName}</span>
                      {d.documentType && (
                        <span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">
                          {d.documentType}
                        </span>
                      )}
                    </div>

                    {(d.status === 'queued' || d.status === 'processing') && (
                      <p className="flex items-center gap-2 text-sm text-slate-600">
                        <Loader2 size={16} className="animate-spin text-safety-600" />
                        AIが読み取り・匿名化・4M分析をしています…
                      </p>
                    )}

                    {d.status === 'failed' && (
                      <div>
                        <p className="flex items-start gap-2 text-sm text-red-700">
                          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                          {d.message}
                        </p>
                        {d.retryable && d.file && (
                          <button
                            onClick={() => retryOne(d)}
                            className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50"
                          >
                            <RotateCw size={13} /> この1枚を再試行
                          </button>
                        )}
                      </div>
                    )}

                    {d.status === 'saved' && (
                      <p className="flex items-center gap-2 text-sm font-medium text-emerald-700">
                        <CheckCircle2 size={16} /> ケースとして登録しました
                      </p>
                    )}

                    {d.status === 'ready' && d.analysis && (
                      <div className="space-y-3">
                        <div>
                          <label className="mb-1 block text-[11px] font-bold text-slate-500">タイトル</label>
                          <input
                            value={d.analysis.title}
                            onChange={(e) => editAnalysis(d.id, { title: e.target.value })}
                            maxLength={30}
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-industrial-900 focus:border-safety-500 focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="mb-1 flex items-center gap-1 text-[11px] font-bold text-slate-500">
                            <Sparkles size={11} className="text-safety-500" />
                            AIが匿名化・整形した本文
                          </label>
                          <textarea
                            value={d.analysis.formattedBody}
                            onChange={(e) => editAnalysis(d.id, { formattedBody: e.target.value })}
                            rows={4}
                            className="w-full resize-y rounded-lg border border-slate-300 px-3 py-2 text-sm leading-relaxed text-slate-800 focus:border-safety-500 focus:outline-none"
                          />
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                          <div>
                            <label className="mb-1 block text-[11px] font-bold text-slate-500">危険度</label>
                            <select
                              value={d.analysis.riskLevel}
                              onChange={(e) => editAnalysis(d.id, { riskLevel: e.target.value as RiskLevel })}
                              className={`rounded-full border px-3 py-1 text-xs font-bold ${RISK_STYLE[d.analysis.riskLevel] || ''}`}
                            >
                              <option value={RiskLevel.HIGH}>High</option>
                              <option value={RiskLevel.MEDIUM}>Medium</option>
                              <option value={RiskLevel.LOW}>Low</option>
                            </select>
                          </div>
                          <div>
                            <label className="mb-1 block text-[11px] font-bold text-slate-500">4M分類</label>
                            <select
                              value={d.analysis.category1}
                              onChange={(e) => editAnalysis(d.id, { category1: e.target.value as Category })}
                              className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700"
                            >
                              <option value={Category.MAN}>{Category.MAN}</option>
                              <option value={Category.MACHINE}>{Category.MACHINE}</option>
                              <option value={Category.MATERIAL}>{Category.MATERIAL}</option>
                              <option value={Category.METHOD}>{Category.METHOD}</option>
                            </select>
                          </div>
                          <div className="min-w-[140px] flex-1">
                            <label className="mb-1 block text-[11px] font-bold text-slate-500">2次カテゴリ</label>
                            <input
                              value={d.analysis.category2}
                              onChange={(e) => editAnalysis(d.id, { category2: e.target.value })}
                              maxLength={20}
                              className="w-full rounded-lg border border-slate-300 px-2 py-1 text-xs text-slate-700"
                            />
                          </div>
                        </div>

                        <button
                          onClick={() => setExpanded((p) => ({ ...p, [d.id]: !p[d.id] }))}
                          className="inline-flex items-center gap-1 text-xs font-bold text-safety-600 hover:text-safety-700"
                        >
                          {expanded[d.id] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          {expanded[d.id] ? '原文・教訓を閉じる' : '原文（読み取り結果）と教訓を確認'}
                        </button>

                        {expanded[d.id] && (
                          <div className="space-y-3 rounded-lg bg-slate-50 p-3">
                            <div>
                              <p className="mb-1 text-[11px] font-bold text-slate-500">
                                画像から読み取った原文（匿名化前・確認用）
                              </p>
                              <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap break-words rounded border border-slate-200 bg-white p-2 text-[11px] leading-relaxed text-slate-600">
{d.ocrText || '(なし)'}
                              </pre>
                              <p className="mt-1 text-[10px] text-slate-400">
                                ※ この原文は<strong>保存されません</strong>（この画面を離れると消えます）。誤読がないかの確認用です。
                                登録されるのは上の匿名化済み本文だけです。
                              </p>
                            </div>
                            <div>
                              <label className="mb-1 block text-[11px] font-bold text-slate-500">教訓・改善提案</label>
                              <textarea
                                value={d.analysis.lesson}
                                onChange={(e) => editAnalysis(d.id, { lesson: e.target.value })}
                                rows={4}
                                className="w-full resize-y rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs leading-relaxed text-slate-700 focus:border-safety-500 focus:outline-none"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex shrink-0 flex-col items-end gap-2">
                    <button
                      onClick={() => remove(d.id)}
                      className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-red-600"
                      aria-label="この取り込みを削除"
                    >
                      {d.status === 'saved' ? <X size={16} /> : <Trash2 size={16} />}
                    </button>
                    {d.status === 'ready' && (
                      <button
                        onClick={() => saveOne(d)}
                        className="whitespace-nowrap rounded-lg bg-industrial-900 px-3 py-1.5 text-xs font-bold text-white hover:bg-slate-800"
                      >
                        これを登録
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {drafts.length === 0 && (
        <div className="mt-6 rounded-xl border border-blue-100 bg-blue-50 p-4">
          <p className="text-sm font-bold text-blue-900">こんな使い方ができます</p>
          <ul className="mt-2 space-y-1 text-xs leading-relaxed text-blue-800">
            <li>・提案箱に溜まった紙のヒヤリハット用紙を、まとめてデジタル化する</li>
            <li>・複合機で紙束を一括スキャンしたPDFを、そのまま1本まるごと投入する</li>
            <li>・手書きのKY表・点検表から、危険の傾向を4Mで可視化する</li>
            <li>・過去の報告を取り込んで、ダッシュボードに実データを入れてから運用を始める</li>
          </ul>
        </div>
      )}
    </div>
  );
};
